/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { createContext, ReactNode, useContext, useId } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Box } from "@mui/material";

const CarouselContext = createContext<{
  carousel: ReturnType<typeof useEmblaCarousel> | null;
  id: string | null;
}>({
  carousel: null,
  id: null,
});

export const useCarousel = () => {
  const { carousel, ...rest } = useContext(CarouselContext);

  return {
    ...rest,
    carouselRef: carousel?.[0],
    carouselApi: carousel?.[1],
  }
};

export type CarouselProviderProps = { children: ReactNode; label?: string; watchDragNonTouch?: boolean } & Parameters<typeof useEmblaCarousel>[0];

export const CarouselProvider = ({ children, label, watchDragNonTouch = false, ...options }: CarouselProviderProps) => {
  const id = useId();

  const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

  const embla = useEmblaCarousel({
    align: "start",
    inViewThreshold: 0.5,
    ...options,
    watchDrag: !watchDragNonTouch && !isTouchDevice ? false : options.watchDrag,
  });

  return (
    <CarouselContext.Provider value={{ carousel: embla, id }}>
      <Box role="region" aria-roledescription="carousel" aria-label={label}>
        {children}
      </Box>
    </CarouselContext.Provider>
  );
};
